// Shared vocabulary and pure helpers for the Employee Shifts module.
//
// WEEKDAYS and BREAK_TYPES here are FALLBACKS only — GET /api/shifts/enums is
// the source of truth and every screen prefers it. They exist so a dropdown
// still renders when that call fails.

// 0 = Sunday … 6 = Saturday, matching Date.prototype.getDay() and the API's
// weekly_off_days array.
export const WEEKDAYS = [
    { value: 0, name: 'SUNDAY', short: 'Sun' },
    { value: 1, name: 'MONDAY', short: 'Mon' },
    { value: 2, name: 'TUESDAY', short: 'Tue' },
    { value: 3, name: 'WEDNESDAY', short: 'Wed' },
    { value: 4, name: 'THURSDAY', short: 'Thu' },
    { value: 5, name: 'FRIDAY', short: 'Fri' },
    { value: 6, name: 'SATURDAY', short: 'Sat' },
];

export const BREAK_TYPES = ['UNPAID', 'PAID'];

// MySQL TINYINTs arrive as 0 / 1, not booleans, so `x === true` silently fails.
// Requests take real booleans, so the round-trip is asymmetric — normalise here.
export const toBool = (v) => v === true || v === 1 || v === '1';

export const prettyEnum = (v) => (v || '').replace(/_/g, ' ');

// ---- Dates -----------------------------------------------------------------
//
// effective_from / effective_to / shift_effective_from are plain YYYY-MM-DD
// with no zone. Parsing them with `new Date(str)` would read them as UTC
// midnight and shift the day for anyone west of Greenwich, so all date maths
// here is done on the string parts instead.

const pad = (n) => String(n).padStart(2, '0');

export const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// A Date pinned to UTC noon: safe to do day arithmetic on without a DST or
// offset surprise.
const parseDateOnly = (dateStr) => {
    if (!dateStr) return null;
    const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(Date.UTC(y, m - 1, d, 12));
};

export const addDays = (dateStr, days) => {
    const d = parseDateOnly(dateStr);
    if (!d) return dateStr;
    d.setUTCDate(d.getUTCDate() + days);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

// Render a date-only value as-is — there is no zone in it to convert.
export const formatDateOnly = (dateStr, opts = { day: '2-digit', month: 'short' }) => {
    const d = parseDateOnly(dateStr);
    if (!d) return '—';
    return d.toLocaleDateString('en-IN', { ...opts, timeZone: 'UTC' });
};

export const formatDateLong = (dateStr) =>
    formatDateOnly(dateStr, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

// ---- Times -----------------------------------------------------------------

// start_time / end_time are wall-clock strings with no zone ("23:00:00").
// There is NO timezone conversion anywhere in this module — only created_at /
// updated_at are UTC instants, and nothing here displays those.
export const formatWallTime = (time) => (time ? String(time).slice(0, 5) : '—');

// A night shift ends the next morning, so the end time gets a +1 marker.
export const shiftTimeRange = (shift) => {
    if (!shift?.start_time) return '';
    const start = formatWallTime(shift.start_time);
    const end = formatWallTime(shift.end_time);
    return `${start} → ${end}${toBool(shift.crosses_midnight) ? ' +1' : ''}`;
};

// ---- Presentation ----------------------------------------------------------

// Pick black or white text for a shift's color_hex so the code stays readable
// on both a pale yellow and a deep navy. Rec. 601 luma, the usual threshold.
export const readableTextOn = (hex) => {
    if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return '#1f2937';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#1f2937' : '#ffffff';
};

// The weekly offs an assignment carries, as "Sun, Wed". Prefers the server's
// weekly_off_day_names and falls back to the numeric array.
export const offDayLabel = (assignment) => {
    const names = assignment?.weekly_off_day_names;
    if (Array.isArray(names) && names.length > 0) {
        return names
            .map(n => WEEKDAYS.find(w => w.name === n)?.short || n.slice(0, 3))
            .join(', ');
    }
    const days = assignment?.weekly_off_days;
    if (!Array.isArray(days) || days.length === 0) return '—';
    return days.map(d => WEEKDAYS.find(w => w.value === d)?.short || d).join(', ');
};

// Two weekly-off selections are the same set — used to decide whether the
// employee form has anything to send at all (see the omit-vs-null rule).
export const sameDays = (a, b) => {
    const left = [...(a || [])].sort((x, y) => x - y);
    const right = [...(b || [])].sort((x, y) => x - y);
    return left.length === right.length && left.every((v, i) => v === right[i]);
};

// ---- Derived shift maths (preview only) ------------------------------------
//
// The API computes and returns span_minutes / scheduled_minutes; display those.
// This mirrors the same maths so the shift FORM can preview the numbers while
// the admin is still typing, before anything has been saved.

export const minutesFromTime = (time) => {
    if (!time) return null;
    const [h, m] = String(time).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
};

export const spanMinutes = (startTime, endTime) => {
    const start = minutesFromTime(startTime);
    const end = minutesFromTime(endTime);
    if (start == null || end == null) return null;
    // end <= start means it crosses midnight; equal times are a legitimate
    // 24-hour shift, not an error.
    return end > start ? end - start : (1440 - start) + end;
};

export const formatMinutes = (mins) => {
    if (mins == null || Number.isNaN(mins)) return '—';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
};
